import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import {
  InputNumber,
  InputNumberValueChangeEvent,
} from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { RadioButton } from "primereact/radiobutton";
import { Checkbox } from "primereact/checkbox";
import { cloudinaryConfig } from "../config/cloudinary";
import "../CSS/AddRealEstate.css";
import axios from "axios";

interface RealEstate {
  id: string;
  title: string;
  description: string;
  price: number;
  location: string;
  propertyType: string;
  bedrooms: number;
  bathrooms: number;
  area: number;
  mainImage: string;
  galleryImages: string[];
  youtubeUrl?: string;
  createdAt: number;
  country: "Lebanon" | "Abidjan";
  type: "realestate";
  showInFlipcard: boolean;
}

const AddRealEstate: React.FC = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mainImage, setMainImage] = useState<File | null>(null);
  const [galleryImages, setGalleryImages] = useState<File[]>([]);
  const [mainImagePreview, setMainImagePreview] = useState<string>("");
  const [galleryPreviews, setGalleryPreviews] = useState<string[]>([]);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [country, setCountry] = useState<"Lebanon" | "Abidjan">("Lebanon");
  const [showInFlipcard, setShowInFlipcard] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: 0,
    location: "",
    propertyType: "",
    bedrooms: 0,
    bathrooms: 0,
    area: 0,
  });

  const propertyTypes = [
    { label: "House", value: "house" },
    { label: "Apartment", value: "apartment" },
    { label: "Condo", value: "condo" },
    { label: "Villa", value: "villa" },
    { label: "Land", value: "land" },
    { label: "Factory", value: "factory" },
    { label: "Store", value: "store" },
    { label: "Office", value: "office" },
    { label: "Warehouse", value: "warehouse" },
    { label: "Commercial Space", value: "commercial" },
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDropdownChange = (e: { value: string }) => {
    setFormData((prev) => ({
      ...prev,
      propertyType: e.value,
    }));
  };

  const handleNumberChange = (
    field: string,
    value: number | null | undefined
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value ?? 0,
    }));
  };

  const handleMainImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      try {
        setUploading(true);
        const uploadedUrl = await uploadToCloudinary(file);
        setMainImage(file);
        setMainImagePreview(uploadedUrl);
      } catch (error) {
        console.error("Error uploading image:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to upload image. Please try again.",
          life: 3000,
        });
      } finally {
        setUploading(false);
      }
    }
  };

  const handleGalleryImagesChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setGalleryImages((prev) => [...prev, ...files]);
      const newPreviews = files.map((file) => URL.createObjectURL(file));
      setGalleryPreviews((prev) => [...prev, ...newPreviews]);
    }
  };

  const removeGalleryImage = (index: number) => {
    setGalleryImages((prev) => prev.filter((_, i) => i !== index));
    setGalleryPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadToCloudinary = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", cloudinaryConfig.uploadPreset);
      formData.append("cloud_name", cloudinaryConfig.cloudName);

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudinaryConfig.cloudName}/image/upload`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || "Failed to upload image");
      }

      const data = await response.json();
      return data.secure_url;
    } catch (error: any) {
      console.error("Error uploading image:", error);
      throw new Error(`Error uploading image: ${error.message}`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (
        !formData.title ||
        !formData.description ||
        !formData.location ||
        !formData.propertyType ||
        !mainImage
      ) {
        throw new Error(
          "Please fill in all required fields and upload a main image"
        );
      }

      let mainImageUrl = "";
      let galleryUrls: string[] = [];

      // Upload main image
      if (mainImage) {
        mainImageUrl = mainImagePreview;
      }

      // Upload gallery images
      if (galleryImages.length > 0) {
        const uploadPromises = galleryImages.map((file) => uploadToCloudinary(file));
        galleryUrls = await Promise.all(uploadPromises);
      }

      const realEstateData = {
        ...formData,
        mainImage: mainImageUrl,
        galleryImages: galleryUrls,
        youtubeUrl,
        country,
        showInFlipcard,
        type: "realestate" as const,
        createdAt: Date.now(),
      };

      if (showInFlipcard) {
        const { data: flipcards } = await axios.get(
          `${process.env.REACT_APP_API}/real-estates?showInFlipcard=true`
        );
        if (flipcards.length >= 5) {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              "Maximum limit of 5 flipcards reached. Please uncheck some posts first.",
            life: 5000,
          });
          setShowInFlipcard(false);
          return;
        }
      }

      await axios.post(
        process.env.REACT_APP_API + "/real-estates",
        realEstateData
      );

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Real estate listing added successfully",
        life: 3000,
      });

      // Reset form
      setFormData({
        title: "",
        description: "",
        price: 0,
        location: "",
        propertyType: "",
        bedrooms: 0,
        bathrooms: 0,
        area: 0,
      });
      setMainImage(null);
      setMainImagePreview("");
      setGalleryImages([]);
      setGalleryPreviews([]);
      setYoutubeUrl("");
      setCountry("Lebanon");
      setShowInFlipcard(false);

      if (country === "Lebanon") {
        navigate("/lebanon-realestate");
      } else {
        navigate("/abidjan-realestate");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail:
          error instanceof Error
            ? error.message
            : "An error occurred while saving",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-property-container">
      <h1>List New Property</h1>
      <form onSubmit={handleSubmit} className="property-form">
        <div className="form-section">
          <label htmlFor="title">Title</label>
          <InputText
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-section">
          <label htmlFor="description">Description</label>
          <InputTextarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={5}
            required
            className="whitespace-pre-wrap w-full"
          />
        </div>

        <div className="form-row">
          <div className="form-section">
            <label htmlFor="price">Price ($)</label>
            <InputNumber
              id="price"
              value={formData.price}
              onValueChange={(e: InputNumberValueChangeEvent) =>
                handleNumberChange("price", e.value)
              }
              mode="currency"
              currency="USD"
              required
            />
          </div>

          <div className="form-section">
            <label htmlFor="location">Location</label>
            <InputText
              id="location"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-section">
            <label htmlFor="propertyType">Property Type</label>
            <Dropdown
              id="propertyType"
              value={formData.propertyType}
              options={propertyTypes}
              onChange={handleDropdownChange}
              placeholder="Select Property Type"
              required
            />
          </div>

          <div className="form-section">
            <label htmlFor="area">Area (sqft)</label>
            <InputNumber
              id="area"
              value={formData.area}
              onValueChange={(e: InputNumberValueChangeEvent) =>
                handleNumberChange("area", e.value)
              }
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-section">
            <label htmlFor="bedrooms">Bedrooms</label>
            <InputNumber
              id="bedrooms"
              value={formData.bedrooms}
              onValueChange={(e: InputNumberValueChangeEvent) =>
                handleNumberChange("bedrooms", e.value)
              }
              required
            />
          </div>

          <div className="form-section">
            <label htmlFor="bathrooms">Bathrooms</label>
            <InputNumber
              id="bathrooms"
              value={formData.bathrooms}
              onValueChange={(e: InputNumberValueChangeEvent) =>
                handleNumberChange("bathrooms", e.value)
              }
              required
            />
          </div>
        </div>

        <div className="form-section">
          <label htmlFor="youtubeUrl">YouTube Video URL (Optional)</label>
          <InputText
            id="youtubeUrl"
            value={youtubeUrl}
            onChange={(e) => setYoutubeUrl(e.target.value)}
            placeholder="Enter YouTube video URL"
          />
        </div>

        <div className="form-section">
          <h3>Location Details</h3>
          <div className="field">
            <label htmlFor="country">Country</label>
            <div className="radio-group">
              <div className="radio-item">
                <RadioButton
                  inputId="Lebanon"
                  name="country"
                  value="Lebanon"
                  onChange={(e) => setCountry(e.value)}
                  checked={country === "Lebanon"}
                />
                <label htmlFor="Lebanon" className="radio-label">
                  Lebanon
                </label>
              </div>
              <div className="radio-item">
                <RadioButton
                  inputId="Abidjan"
                  name="country"
                  value="Abidjan"
                  onChange={(e) => setCountry(e.value)}
                  checked={country === "Abidjan"}
                />
                <label htmlFor="Abidjan" className="radio-label">
                  Abidjan
                </label>
              </div>
            </div>
          </div>
          <div className="field">
            <label htmlFor="mainImage">Main Image</label>
            <input
              type="file"
              id="mainImage"
              accept="image/*"
              onChange={handleMainImageChange}
              required
            />
            {mainImagePreview && (
              <img
                src={mainImagePreview}
                alt="Main preview"
                className="image-preview"
              />
            )}
          </div>
        </div>

        <div className="form-section">
          <label htmlFor="galleryImages">Gallery Images</label>
          <input
            type="file"
            id="galleryImages"
            accept="image/*"
            multiple
            onChange={handleGalleryImagesChange}
          />
          <div className="gallery-previews">
            {galleryPreviews.map((preview, index) => (
              <div key={index} className="gallery-preview-container">
                <img
                  src={preview}
                  alt={`Gallery preview ${index + 1}`}
                  className="gallery-preview"
                />
                <Button
                  icon="pi pi-times"
                  className="p-button-rounded p-button-danger gallery-remove"
                  onClick={() => removeGalleryImage(index)}
                />
              </div>
            ))}
          </div>
        </div>

        <div
          className="p-field-checkbox"
          style={{ marginTop: "1rem", marginBottom: "1rem" }}
        >
          <Checkbox
            inputId="showInFlipcard"
            checked={showInFlipcard}
            onChange={(e) => setShowInFlipcard(e.target.checked ?? false)}
          />
          <label
            htmlFor="showInFlipcard"
            className="p-checkbox-label"
            style={{ marginLeft: "0.5rem" }}
          >
            Show in Home Flipcard Section
          </label>
        </div>

        <Button
          type="submit"
          label={loading ? "Listing Property..." : "List Property"}
          icon="pi pi-check"
          disabled={loading}
        />
        {loading && (
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
        )}
      </form>

      <Toast ref={toast} />
    </div>
  );
};

export default AddRealEstate;
